/**
 * SelectExamples.tsx
 * Comprehensive examples of HeroUI Select component
 * 
 * Showcases all HeroUI Select features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Select, SelectItem, SelectSection } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Avatar } from '@heroui/react';
import { 
  Building2, 
  User, 
  TrendingUp,
  Star,
  Crown,
  Target,
  Award,
  Trophy,
  Zap
} from 'lucide-react';

interface BrokerOption {
  key: string;
  name: string;
  logo: string;
  region: string;
  fees: string;
  assets: string[];
}

interface UserOption {
  key: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
}

interface AssetOption {
  key: string;
  name: string;
  symbol: string;
  sector: string;
  price: string;
  change: number;
}

export const SelectExamples: React.FC = () => {
  const [selectedValues, setSelectedValues] = useState<Record<string, unknown>>({
    basic: '',
    bordered: '',
    underlined: '',
    faded: '',
    flat: '',
    small: '',
    medium: '',
    large: '',
    primary: '',
    secondary: '',
    success: '',
    warning: '',
    danger: '',
    required: '',
    disabled: 'disabled-option',
    multiple: new Set([]),
    controlled: new Set(['option1']),
    brokers: '',
    users: '',
    assets: '',
    strategies: new Set([]),
    largeDataset: ''
  });

  const brokerOptions: BrokerOption[] = [
    { key: "ib", name: "Interactive Brokers", logo: "/brokers/ib.png", region: "Global", fees: "$1/trade", assets: ["Stocks", "Options", "Futures", "Forex"] },
    { key: "td", name: "TD Ameritrade", logo: "/brokers/td.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "schwab", name: "Charles Schwab", logo: "/brokers/schwab.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options", "Mutual Funds"] },
    { key: "fidelity", name: "Fidelity", logo: "/brokers/fidelity.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "etrade", name: "E*TRADE", logo: "/brokers/etrade.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "webull", name: "Webull", logo: "/brokers/webull.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options", "Crypto"] }
  ];

  const userOptions: UserOption[] = [
    { key: "john", name: "John Doe", email: "john@example.com", role: "Administrator", avatar: "/users/john.jpg" },
    { key: "jane", name: "Jane Smith", email: "jane@example.com", role: "Editor", avatar: "/users/jane.jpg" },
    { key: "bob", name: "Bob Wilson", email: "bob@example.com", role: "Viewer", avatar: "/users/bob.jpg" },
    { key: "alice", name: "Alice Johnson", email: "alice@example.com", role: "Moderator", avatar: "/users/alice.jpg" }
  ];

  const assetOptions: AssetOption[] = [
    { key: "aapl", name: "Apple Inc.", symbol: "AAPL", sector: "Technology", price: "$175.43", change: 2.1 },
    { key: "googl", name: "Alphabet Inc.", symbol: "GOOGL", sector: "Technology", price: "$142.56", change: 1.8 },
    { key: "msft", name: "Microsoft Corp.", symbol: "MSFT", sector: "Technology", price: "$378.85", change: -0.5 },
    { key: "amzn", name: "Amazon.com Inc.", symbol: "AMZN", sector: "Consumer Discretionary", price: "$154.23", change: 3.2 },
    { key: "tsla", name: "Tesla Inc.", symbol: "TSLA", sector: "Automotive", price: "$248.50", change: -1.2 },
    { key: "nvda", name: "NVIDIA Corp.", symbol: "NVDA", sector: "Technology", price: "$875.28", change: 4.7 }
  ];

  // Generate large dataset for virtualization example
  interface LargeDataItem {
    key: string;
    name: string;
    value: string;
    category: string;
  }

  const largeDataset: LargeDataItem[] = Array.from({ length: 1000 }, (_, i) => ({
    key: `item-${i}`,
    name: `Large Dataset Item ${i + 1}`,
    value: `Value ${i + 1}`,
    category: `Category ${Math.floor(i / 100) + 1}`
  }));

  const handleSelectionChange = (key: string, selection: unknown) => {
    setSelectedValues(prev => ({ ...prev, [key]: selection }));
  };

  // Helper to create proper Set for selectedKeys
  const getSelectedKeysSet = (key: string): Set<string> => {
    const value = selectedValues[key];
    if (value instanceof Set) {
      return value as Set<string>;
    }
    return value ? new Set([String(value)]) : new Set();
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Select Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Select component with automatic theme integration
          </p>
        </div>

        {/* Basic Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Select Variants</h2>
              <Badge color="primary" variant="flat" size="sm">4 variants</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                variant="flat"
                label="Flat Variant"
                placeholder="Choose an option"
                selectedKeys={getSelectedKeysSet('flat')}
                onSelectionChange={(keys) => handleSelectionChange('flat', Array.from(keys)[0] || '')}
                description="Flat select with subtle background"
              >
                <SelectItem key="option1">Option 1</SelectItem>
                <SelectItem key="option2">Option 2</SelectItem>
                <SelectItem key="option3">Option 3</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Bordered Variant"
                placeholder="Choose an option"
                selectedKeys={getSelectedKeysSet('bordered')}
                onSelectionChange={(keys) => handleSelectionChange('bordered', Array.from(keys)[0] || '')}
                description="Select with visible border"
              >
                <SelectItem key="option1">Option 1</SelectItem>
                <SelectItem key="option2">Option 2</SelectItem>
                <SelectItem key="option3">Option 3</SelectItem>
              </Select>
              
              <Select
                variant="underlined"
                label="Underlined Variant"
                placeholder="Choose an option"
                selectedKeys={getSelectedKeysSet('underlined')}
                onSelectionChange={(keys) => handleSelectionChange('underlined', Array.from(keys)[0] || '')}
                description="Select with bottom border only"
              >
                <SelectItem key="option1">Option 1</SelectItem>
                <SelectItem key="option2">Option 2</SelectItem>
                <SelectItem key="option3">Option 3</SelectItem>
              </Select>
              
              <Select
                variant="faded"
                label="Faded Variant"
                placeholder="Choose an option"
                selectedKeys={getSelectedKeysSet('faded')}
                onSelectionChange={(keys) => handleSelectionChange('faded', Array.from(keys)[0] || '')}
                description="Faded appearance select"
              >
                <SelectItem key="option1">Option 1</SelectItem>
                <SelectItem key="option2">Option 2</SelectItem>
                <SelectItem key="option3">Option 3</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Select Sizes</h2>
              <Badge color="warning" variant="flat" size="sm">3 sizes</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Select
                size="sm"
                variant="bordered"
                label="Small"
                placeholder="Small select"
                selectedKeys={getSelectedKeysSet('small')}
                onSelectionChange={(keys) => handleSelectionChange('small', Array.from(keys)[0] || '')}
              >
                <SelectItem key="small1">Small Option 1</SelectItem>
                <SelectItem key="small2">Small Option 2</SelectItem>
                <SelectItem key="small3">Small Option 3</SelectItem>
              </Select>
              
              <Select
                size="md"
                variant="bordered"
                label="Medium"
                placeholder="Medium select"
                selectedKeys={getSelectedKeysSet('medium')}
                onSelectionChange={(keys) => handleSelectionChange('medium', Array.from(keys)[0] || '')}
              >
                <SelectItem key="medium1">Medium Option 1</SelectItem>
                <SelectItem key="medium2">Medium Option 2</SelectItem>
                <SelectItem key="medium3">Medium Option 3</SelectItem>
              </Select>
              
              <Select
                size="lg"
                variant="bordered"
                label="Large"
                placeholder="Large select"
                selectedKeys={getSelectedKeysSet('large')}
                onSelectionChange={(keys) => handleSelectionChange('large', Array.from(keys)[0] || '')}
              >
                <SelectItem key="large1">Large Option 1</SelectItem>
                <SelectItem key="large2">Large Option 2</SelectItem>
                <SelectItem key="large3">Large Option 3</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Select Colors</h2>
              <Badge color="danger" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Select
                color="primary"
                variant="bordered"
                label="Primary"
                placeholder="Primary color"
                selectedKeys={getSelectedKeysSet('primary')}
                onSelectionChange={(keys) => handleSelectionChange('primary', Array.from(keys)[0] || '')}
              >
                <SelectItem key="primary1">Primary Option 1</SelectItem>
                <SelectItem key="primary2">Primary Option 2</SelectItem>
              </Select>
              
              <Select
                color="secondary"
                variant="bordered"
                label="Secondary"
                placeholder="Secondary color"
                selectedKeys={getSelectedKeysSet('secondary')}
                onSelectionChange={(keys) => handleSelectionChange('secondary', Array.from(keys)[0] || '')}
              >
                <SelectItem key="secondary1">Secondary Option 1</SelectItem>
                <SelectItem key="secondary2">Secondary Option 2</SelectItem>
              </Select>
              
              <Select
                color="success"
                variant="bordered"
                label="Success"
                placeholder="Success color"
                selectedKeys={getSelectedKeysSet('success')}
                onSelectionChange={(keys) => handleSelectionChange('success', Array.from(keys)[0] || '')}
              >
                <SelectItem key="success1">Success Option 1</SelectItem>
                <SelectItem key="success2">Success Option 2</SelectItem>
              </Select>
              
              <Select
                color="warning"
                variant="bordered"
                label="Warning"
                placeholder="Warning color"
                selectedKeys={getSelectedKeysSet('warning')}
                onSelectionChange={(keys) => handleSelectionChange('warning', Array.from(keys)[0] || '')}
              >
                <SelectItem key="warning1">Warning Option 1</SelectItem>
                <SelectItem key="warning2">Warning Option 2</SelectItem>
              </Select>
              
              <Select
                color="danger"
                variant="bordered"
                label="Danger"
                placeholder="Danger color"
                selectedKeys={getSelectedKeysSet('danger')}
                onSelectionChange={(keys) => handleSelectionChange('danger', Array.from(keys)[0] || '')}
              >
                <SelectItem key="danger1">Danger Option 1</SelectItem>
                <SelectItem key="danger2">Danger Option 2</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Selection Modes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Selection Modes</h2>
              <Badge color="success" variant="flat" size="sm">Single & Multiple</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                variant="bordered"
                label="Single Selection"
                placeholder="Choose one option"
                selectionMode="single"
                selectedKeys={getSelectedKeysSet('controlled')}
                onSelectionChange={(keys) => handleSelectionChange('controlled', keys)}
                description="Default single selection mode"
              >
                <SelectItem key="option1">Option 1</SelectItem>
                <SelectItem key="option2">Option 2</SelectItem>
                <SelectItem key="option3">Option 3</SelectItem>
                <SelectItem key="option4">Option 4</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Multiple Selection"
                placeholder="Choose multiple options"
                selectionMode="multiple"
                selectedKeys={getSelectedKeysSet('multiple')}
                onSelectionChange={(keys) => handleSelectionChange('multiple', keys)}
                description="Select multiple options at once"
              >
                <SelectItem key="multi1">Multiple Option 1</SelectItem>
                <SelectItem key="multi2">Multiple Option 2</SelectItem>
                <SelectItem key="multi3">Multiple Option 3</SelectItem>
                <SelectItem key="multi4">Multiple Option 4</SelectItem>
                <SelectItem key="multi5">Multiple Option 5</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Select with Sections */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Grouped Options</h2>
              <Badge color="secondary" variant="flat" size="sm">Sections</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Select
                variant="bordered"
                label="Trading Strategies"
                placeholder="Choose a strategy"
                color="primary"
                selectedKeys={getSelectedKeysSet('strategies')}
                onSelectionChange={(keys) => handleSelectionChange('strategies', keys)}
                selectionMode="multiple"
                description="Strategies grouped by risk level"
              >
                <SelectSection title="Conservative Strategies" showDivider>
                  <SelectItem key="buy-hold" startContent={<Target className="w-4 h-4" />}>
                    Buy and Hold
                  </SelectItem>
                  <SelectItem key="dca" startContent={<TrendingUp className="w-4 h-4" />}>
                    Dollar Cost Averaging
                  </SelectItem>
                  <SelectItem key="dividend" startContent={<Award className="w-4 h-4" />}>
                    Dividend Investing
                  </SelectItem>
                </SelectSection>
                
                <SelectSection title="Moderate Strategies" showDivider>
                  <SelectItem key="swing" startContent={<Star className="w-4 h-4" />}>
                    Swing Trading
                  </SelectItem>
                  <SelectItem key="momentum" startContent={<Zap className="w-4 h-4" />}>
                    Momentum Trading
                  </SelectItem>
                  <SelectItem key="covered-calls" startContent={<Crown className="w-4 h-4" />}>
                    Covered Calls
                  </SelectItem>
                </SelectSection>
                
                <SelectSection title="Aggressive Strategies">
                  <SelectItem key="day-trading" startContent={<Trophy className="w-4 h-4" />}>
                    Day Trading
                  </SelectItem>
                  <SelectItem key="scalping" startContent={<Zap className="w-4 h-4" />}>
                    Scalping
                  </SelectItem>
                  <SelectItem key="options" startContent={<Target className="w-4 h-4" />}>
                    Options Trading
                  </SelectItem>
                </SelectSection>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Rich Content */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Rich Content</h2>
              <Badge variant="flat" size="sm">Advanced</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brokers with logos and info */}
              <Select
                variant="bordered"
                label="Select Broker"
                placeholder="Choose your broker"
                selectedKeys={getSelectedKeysSet('brokers')}
                onSelectionChange={(keys) => handleSelectionChange('brokers', Array.from(keys)[0] || '')}
                startContent={<Building2 className="w-4 h-4" />}
              >
                {brokerOptions.map((broker) => (
                  <SelectItem 
                    key={broker.key}
                    description={`${broker.region} • ${broker.fees} • ${broker.assets.join(", ")}`}
                    startContent={<div className="w-6 h-6 bg-default-200 rounded-full flex items-center justify-center text-xs font-semibold">{broker.name.charAt(0)}</div>}
                  >
                    {broker.name}
                  </SelectItem>
                ))}
              </Select>

              {/* Users with avatars */}
              <Select
                variant="bordered"
                label="Assign User"
                placeholder="Select a user"
                selectedKeys={getSelectedKeysSet('users')}
                onSelectionChange={(keys) => handleSelectionChange('users', Array.from(keys)[0] || '')}
                startContent={<User className="w-4 h-4" />}
              >
                {userOptions.map((user) => (
                  <SelectItem 
                    key={user.key}
                    description={`${user.role} • ${user.email}`}
                    startContent={
                      <Avatar 
                        src={user.avatar} 
                        name={user.name}
                        size="sm" 
                        className="w-6 h-6"
                      />
                    }
                  >
                    {user.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* States & Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">States & Features</h2>
              <Badge variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                variant="bordered"
                label="Required Field"
                placeholder="This field is required"
                isRequired
                selectedKeys={getSelectedKeysSet('required')}
                onSelectionChange={(keys) => handleSelectionChange('required', Array.from(keys)[0] || '')}
                color="primary"
                errorMessage="Please select an option"
              >
                <SelectItem key="req1">Required Option 1</SelectItem>
                <SelectItem key="req2">Required Option 2</SelectItem>
                <SelectItem key="req3">Required Option 3</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Disabled Select"
                isDisabled
                selectedKeys={getSelectedKeysSet('disabled')}
                description="This select is disabled"
              >
                <SelectItem key="disabled-option">Disabled Option</SelectItem>
                <SelectItem key="another-disabled">Another Option</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Virtualized Large Dataset */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Virtualized Select</h2>
              <Badge color="warning" variant="flat" size="sm">1000+ items</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Select
                variant="bordered"
                label="Large Dataset"
                placeholder="Search through 1000 items"
                items={largeDataset}
                isVirtualized
                selectedKeys={getSelectedKeysSet('largeDataset')}
                onSelectionChange={(keys) => handleSelectionChange('largeDataset', Array.from(keys)[0] || '')}
                description="Efficiently renders large lists with virtualization"
                color="success"
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(item: any) => (
                  <SelectItem key={item.key} textValue={item.name}>
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      <span className="text-small text-default-400">{item.category}</span>
                    </div>
                  </SelectItem>
                )}
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Assets Example */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Dynamic Assets</h2>
              <Badge color="success" variant="flat" size="sm">Real-time data</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Select
                variant="bordered"
                label="Select Asset"
                placeholder="Choose an asset to trade"
                items={assetOptions}
                selectedKeys={getSelectedKeysSet('assets')}
                onSelectionChange={(keys) => handleSelectionChange('assets', Array.from(keys)[0] || '')}
                startContent={<TrendingUp className="w-4 h-4" />}
                description="Assets with real-time pricing"
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(asset: any) => (
                  <SelectItem 
                    key={asset.key} 
                    textValue={`${asset.name} (${asset.symbol})`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex flex-col">
                        <span className="text-small font-semibold">{asset.symbol}</span>
                        <span className="text-tiny text-default-500">{asset.name}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-small font-semibold">{asset.price}</span>
                        <span className={`text-tiny ${asset.change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {asset.change >= 0 ? '+' : ''}{asset.change}%
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                )}
              </Select>
            </div>
          </CardBody>
        </Card>

        {/* Label Placements */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Label Placements</h2>
              <Badge color="secondary" variant="flat" size="sm">4 positions</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Select
                variant="bordered"
                label="Inside Label"
                placeholder="Choose option..."
                labelPlacement="inside"
              >
                <SelectItem key="inside1">Inside Option 1</SelectItem>
                <SelectItem key="inside2">Inside Option 2</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Outside Label"
                placeholder="Choose option..."
                labelPlacement="outside"
              >
                <SelectItem key="outside1">Outside Option 1</SelectItem>
                <SelectItem key="outside2">Outside Option 2</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Outside Left"
                placeholder="Choose option..."
                labelPlacement="outside-left"
              >
                <SelectItem key="left1">Left Option 1</SelectItem>
                <SelectItem key="left2">Left Option 2</SelectItem>
              </Select>
              
              <Select
                variant="bordered"
                label="Outside Top"
                placeholder="Choose option..."
                labelPlacement="outside-left"
              >
                <SelectItem key="top1">Top Option 1</SelectItem>
                <SelectItem key="top2">Top Option 2</SelectItem>
              </Select>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};